/**
 * 프로젝트 JSON 형식 — 스키마 버전·래퍼·형식 판별·직렬화·다운로드.
 *
 * 진입 청크(워크스페이스 화면의 «JSON 에서 만들기»)가 쓰므로 편집기·해석 도메인을 끌어오지 않는다. 옛 형식(그래프
 * 하나)은 판별만 하고 역직렬화하지 않는다 — 만들지 않으므로(옛 역직렬화기 projectFileHelpers 는 고아가 되어 삭제).
 */
import type { ProjectData } from '@/types/project';

/** 현재 스키마 버전 - 스키마 구조 변경 시 bump */
export const VSMR_SCHEMA_VERSION = 2;

/** Export JSON 메타데이터 */
export interface VsmrMeta {
  version: number;
  appVersion: string;
  exportedAt: string;
  projectName: string;
}

/** Export JSON 래퍼 구조 */
export interface VsmrProjectFile {
  _vsmr_meta_: VsmrMeta;
  data: ProjectData;
}

/** 파일 판별 결과 */
export type ProjectFileType = 'vsmr' | 'legacy-mars' | 'raw-projectdata' | 'unknown';

/**
 * ProjectData를 _vsmr_meta_ 래퍼로 감싸서 JSON 문자열로 직렬화
 */
export function serializeProjectData(
  projectData: ProjectData,
  projectName: string,
): string {
  const wrapped: VsmrProjectFile = {
    _vsmr_meta_: {
      version: VSMR_SCHEMA_VERSION,
      appVersion: '0.1.0',
      exportedAt: new Date().toISOString(),
      projectName,
    },
    data: projectData,
  };

  return JSON.stringify(wrapped, null, 2);
}

/**
 * 프로젝트 JSON 파일을 로컬에 다운로드
 */
export function downloadProjectData(
  projectData: ProjectData,
  projectName: string,
): void {
  const jsonContent = serializeProjectData(projectData, projectName);
  const blob = new Blob([jsonContent], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  const sanitizedName = projectName.trim().replace(/[<>:"/\\|?*]/g, '_').replace(/\s+/g, '_');
  const filename = `${sanitizedName}.json`;

  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();

  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * JSON 파일 형식 자동 판별
 */
export function detectProjectFileType(json: Record<string, unknown>): ProjectFileType {
  if (json._vsmr_meta_ && typeof json._vsmr_meta_ === 'object' && json.data) {
    return 'vsmr';
  }
  if (json.metadata && json.nodes && json.edges) {
    return 'legacy-mars';
  }
  if (json.models && Array.isArray(json.models)) {
    return 'raw-projectdata';
  }
  return 'unknown';
}

/**
 * _vsmr_meta_ 버전 호환성 체크
 * @returns null이면 호환, 문자열이면 에러 메시지
 */
export function checkVsmrCompatibility(meta: VsmrMeta): string | null {
  if (meta.version > VSMR_SCHEMA_VERSION) {
    return `이 파일은 최신 버전(v${meta.version})에서 내보낸 것입니다. 앱을 업데이트해주세요. (현재: v${VSMR_SCHEMA_VERSION})`;
  }
  return null;
}

